import { PDFDocument, degrees, rgb, StandardFonts } from 'pdf-lib';
const API_URL = import.meta.env.VITE_API_URL;
const API_TOKEN = 'Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq';

/**
 * Downloads raw bytes for a given URL.
 */
async function fetchBytes(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch resource from ${url}`);
  }
  return await response.arrayBuffer();
}

/**
 * Downloads raw bytes for a given PDF URL.
 */
async function fetchPDFBytes(url) {
  return fetchBytes(url);
}

/**
 * Fetches the UPS labels PDF for a shipment from the API.
 * Returns an object with the PDF bytes and label type information.
 */
async function fetchShipmentPdf(planId, shipmentId) {
  const url = `https://app.yyzprep.ca/api/fba-transport/v2024/plans/${planId}/labels?shipment_id=${shipmentId}&api_token=${API_TOKEN}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch labels PDF for shipment: ${shipmentId}`);
  }
  const data = await response.json();
  
  let label = null;
  let isAmazonBoxLabels = false;
  
  // Primary search: Look for 8.5" package labels
  label = data.data?.find(
    (lbl) => lbl.name.includes('8.5') && lbl.type === 'package'
  );
  
  // Fallback search: Look for Amazon Box Labels with 4" x 6" format
  if (!label?.url) {
    console.log(`No 8.5" package label found for shipment ${shipmentId}, trying Amazon Box Labels fallback...`);
    
    // Look for labels matching the pattern: Amazon Box Labels (Box 1 - [number]) (4" x 6")
    label = data.data?.find(
      (lbl) => {
        const amazonBoxPattern = /Amazon Box Labels \(Box \d+ - \d+\) \(4" x 6"\)/;
        return amazonBoxPattern.test(lbl.name);
      }
    );
    
    if (label?.url) {
      console.log(`Found Amazon Box Labels fallback for shipment ${shipmentId}: ${label.name}`);
      isAmazonBoxLabels = true;
    }
  }
  
  if (!label?.url) {
    throw new Error(`No suitable label found for shipment: ${shipmentId}`);
  }
  
  const proxyUrl = `${API_URL}/api/fetch-pdf?url=${encodeURIComponent(label.url)}`;
  const pdfBytes = await fetchPDFBytes(proxyUrl);
  
  return {
    pdfBytes,
    isAmazonBoxLabels,
    labelName: label.name
  };
}

/**
 * Fetches the plan data (which includes box_groups) for a given planId.
 */
async function fetchPlanData(planId) {
  const url = `https://app.yyzprep.ca/api/fba-transport/v2024/plans/${planId}?api_token=${API_TOKEN}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' }
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch plan data for plan: ${planId}`);
  }
  return response.json().then(data => data.data);
}

/**
 * Logs out the contents of each internal box group.
 */
function logInternalBoxContents(planData) {
  if (!planData.box_groups || planData.box_groups.length === 0) {
    console.log("No box groups available in the plan data.");
    return;
  }
  console.log("Listing internal box contents:");
  planData.box_groups.forEach(group => {
    console.log(`\nBox Group ID: ${group.id}`);
    console.log(`Box Numbers: ${group.box_numbers.join(", ")}`);
    if (group.items && group.items.length > 0) {
      console.log("Items:");
      group.items.forEach(item => {
        console.log(
          ` • Plan Item ID: ${item.plan_item_id}, SKU: ${item.msku || "N/A"}, Quantity: ${item.quantity}`
        );
      });
    } else {
      console.log("No items in this box group.");
    }
  });
}

/**
 * Crops and rotates a label from a specific page of sourceDoc into targetDoc.
 */
async function cropAndRotateLabel(sourceDoc, pageIndex, cropBox, targetDoc) {
  const { x, y, width, height } = cropBox;
  const [copiedPage] = await targetDoc.copyPages(sourceDoc, [pageIndex]);
  copiedPage.setCropBox(x, y, width, height);
  copiedPage.setMediaBox(x, y, width, height);
  const scaleFactor = 0.84;
  copiedPage.scale(scaleFactor, scaleFactor);
  copiedPage.setRotation(degrees(90));
  // Swap width and height to preserve portrait orientation.
  copiedPage.setSize(height, width);
  targetDoc.addPage(copiedPage);
}

/**
 * Converts an 8.5x11 PDF (assumed to have 2 labels per physical page)
 * to a new PDF with 4x6 pages.
 */
async function process8x11Page(pdfDoc) {
  const newPdfDoc = await PDFDocument.create();
  const pages = pdfDoc.getPages();
  const label1CropBox = { x: 10, y: 34, width: 288, height: 432 };
  const label2CropBox = { x: 15, y: 342, width: 288, height: 432 };

  for (let i = 0; i < pages.length; i++) {
    try {
      await cropAndRotateLabel(pdfDoc, i, label1CropBox, newPdfDoc);
    } catch (error) {
      console.error(`Error processing Label 1 on Page ${i + 1}: ${error.message}`);
    }
    try {
      await cropAndRotateLabel(pdfDoc, i, label2CropBox, newPdfDoc);
    } catch (error) {
      console.error(`Error processing Label 2 on Page ${i + 1}: ${error.message}`);
    }
  }
  return newPdfDoc;
}

/**
 * Given the plan data, builds the full ordered list of internal box numbers.
 */
function buildOrderedBoxNumbers(planData) {
  const ordered = [];
  planData.box_groups.forEach(group => {
    group.box_numbers.forEach(boxNum => ordered.push(boxNum));
  });
  return ordered;
}

/**
 * Processes one shipment using the internal plan ordering.
 * Returns lightweight metadata only - PDF fetching happens later per-entry.
 */
async function processShipmentUsingPlanMapping(shipment, planData, onProgress) {
  // Extract and map internal box numbers to UPS box numbers
  const boxMappings = shipment.boxes.map(box => {
    const internalBoxNumber = parseInt(box.box_number, 10);
    const amazonBoxId = box.amazon_box_id;
    const upsBoxNumberStr = amazonBoxId.slice(-6);
    const upsBoxNumber = parseInt(upsBoxNumberStr, 10);
    if (isNaN(upsBoxNumber)) {
      throw new Error(`Invalid amazon_box_id for box_number ${internalBoxNumber}: ${amazonBoxId}`);
    }
    return {
      internalBoxNumber,
      upsBoxNumber
    };
  });

  // Sort boxes by internalBoxNumber ascending
  boxMappings.sort((a, b) => a.internalBoxNumber - b.internalBoxNumber);
  console.log("Box mappings (sorted by internal box number):", boxMappings);

  // Return lightweight metadata - don't load PDF yet
  const miniEntries = [];
  for (const mapping of boxMappings) {
    const { internalBoxNumber, upsBoxNumber } = mapping;

    miniEntries.push({
      box_number: internalBoxNumber,
      correctedBoxNumber: internalBoxNumber,
      shipmentIndex: internalBoxNumber,
      upsBoxNumber: upsBoxNumber,
      shipmentId: shipment.shipment_id,
      planId: planData.id,
      boxMappingsLength: boxMappings.length
    });
  }

  return miniEntries;
}

/**
 * Processes all shipments using the internal plan mapping.
 * Returns lightweight metadata for all boxes to be sorted before merging.
 */
async function processAllShipmentsUsingPlanMapping(placementOption, planData, onProgress) {
  let allEntries = [];
  for (let i = 0; i < placementOption.shipments.length; i++) {
    const shipment = placementOption.shipments[i];
    onProgress?.(`Mapping shipment ${i + 1} of ${placementOption.shipments.length}...`);
    try {
      const entries = await processShipmentUsingPlanMapping(shipment, planData, onProgress);
      allEntries = allEntries.concat(entries);
    } catch (err) {
      console.error(`Error processing shipment ${shipment.shipment_id}: ${err.message}`);
    }
  }
  return allEntries;
}

/**
 * Merges all box entries into one final PDF using batch copyPages approach.
 * Sorts globally by correctedBoxNumber, then processes with batched copyPages per shipment.
 * This prevents pdf-lib from duplicating shared resources (fonts, images).
 */
async function mergeAllBoxes(allBoxEntries, onProgress) {
  onProgress?.("Merging PDFs...");

  // Sort ALL entries by correctedBoxNumber globally
  allBoxEntries.sort((a, b) => {
    const aStr = a.correctedBoxNumber.toString();
    const bStr = b.correctedBoxNumber.toString();
    return aStr.localeCompare(bStr, undefined, { numeric: true });
  });
  console.log("Final sorted entries:", allBoxEntries);

  const finalPdf = await PDFDocument.create();

  // Cache for loaded shipment PDFs - key: shipmentId, value: {pdfDoc, isAmazonBoxLabels, wasConverted}
  const shipmentPdfCache = new Map();

  // Pre-load and process all unique shipments, then copy pages in batches
  const uniqueShipments = [...new Set(allBoxEntries.map(e => e.shipmentId))];

  for (const shipmentId of uniqueShipments) {
    const shipmentEntries = allBoxEntries.filter(e => e.shipmentId === shipmentId);
    const firstEntry = shipmentEntries[0];

    onProgress?.(`Loading shipment ${shipmentId}...`);

    // Fetch and load the shipment PDF
    const { pdfBytes, isAmazonBoxLabels } = await fetchShipmentPdf(firstEntry.planId, shipmentId);
    let pdfDoc = await PDFDocument.load(pdfBytes);

    const shipmentCount = firstEntry.boxMappingsLength;
    const expectedPagesByShipment = shipmentCount * 2;
    const pageCount = pdfDoc.getPageCount();
    let wasConverted = false;

    // Handle 8.5x11 conversion if needed
    if (!isAmazonBoxLabels) {
      if (pageCount === expectedPagesByShipment) {
        console.log(`Shipment ${shipmentId} has ${pageCount} pages as expected.`);
      } else if (pageCount === shipmentCount) {
        console.log(`Shipment ${shipmentId} has one page per box. Converting using 2-label crop.`);
        pdfDoc = await process8x11Page(pdfDoc);
        wasConverted = true;
        console.log("Converted PDF page count:", pdfDoc.getPageCount());
      } else {
        console.warn(`Unexpected PDF layout for shipment ${shipmentId}. Page count: ${pageCount}.`);
      }
    }

    // Collect ALL page indices needed from this shipment across ALL entries
    const pageIndicesToCopy = [];
    const indexMapping = new Map(); // Maps entry -> indices in copiedPages array

    for (const entry of shipmentEntries) {
      if (isAmazonBoxLabels) {
        const pageIndex = entry.upsBoxNumber - 1;
        const startIdx = pageIndicesToCopy.length;
        pageIndicesToCopy.push(pageIndex);
        indexMapping.set(entry, {
          indices: [startIdx],
          pageCount: 1,
          wasConverted
        });
      } else {
        const finalPageCount = pdfDoc.getPageCount();
        const upsPageIndex = (entry.upsBoxNumber - 1) * 2;
        const fbaPageIndex = (entry.upsBoxNumber - 1) * 2 + 1;

        if (upsPageIndex >= finalPageCount || fbaPageIndex >= finalPageCount) {
          console.error(`For box ${entry.correctedBoxNumber} (UPS Box ${entry.upsBoxNumber}), page indices [${upsPageIndex}, ${fbaPageIndex}] exceed total ${finalPageCount}.`);
          continue;
        }

        const startIdx = pageIndicesToCopy.length;
        pageIndicesToCopy.push(upsPageIndex, fbaPageIndex);
        indexMapping.set(entry, {
          indices: [startIdx, startIdx + 1],
          pageCount: 2,
          wasConverted
        });
      }
    }

    // CRITICAL: Batch copy ALL pages from this shipment at once
    // This prevents pdf-lib from duplicating shared resources (fonts, images)
    onProgress?.(`Copying ${pageIndicesToCopy.length} pages from shipment ${shipmentId}...`);
    const copiedPages = await finalPdf.copyPages(pdfDoc, pageIndicesToCopy);

    // Store the copied pages and metadata
    shipmentPdfCache.set(shipmentId, {
      copiedPages,
      indexMapping,
      isAmazonBoxLabels
    });
  }

  // Now add pages to finalPdf in the globally sorted order
  let processedBoxes = 0;
  for (const entry of allBoxEntries) {
    processedBoxes++;
    if (processedBoxes % 10 === 0) {
      onProgress?.(`Adding labels: ${processedBoxes}/${allBoxEntries.length}...`);
    }

    console.log(`Adding box number: ${entry.correctedBoxNumber}`);

    const shipmentData = shipmentPdfCache.get(entry.shipmentId);
    const mapping = shipmentData.indexMapping.get(entry);

    if (!mapping) {
      console.error(`No mapping found for box ${entry.correctedBoxNumber}`);
      continue;
    }

    if (mapping.pageCount === 1) {
      // Amazon Box Labels - single page
      const copiedPage = shipmentData.copiedPages[mapping.indices[0]];
      finalPdf.addPage(copiedPage);
    } else {
      // Standard UPS labels - 2 pages
      const copiedUpsPage = shipmentData.copiedPages[mapping.indices[0]];
      const copiedFbaPage = shipmentData.copiedPages[mapping.indices[1]];

      // Add stamping if the PDF was converted from 8.5x11
      if (mapping.wasConverted) {
        const helveticaFont = await finalPdf.embedFont(StandardFonts.Helvetica);
        const stampText = `YYZ Box ${entry.correctedBoxNumber}`;

        const textOptionsUps = {
          x: 373,
          y: 150,
          size: 12,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          rotate: degrees(90)
        };
        copiedUpsPage.drawText(stampText, textOptionsUps);

        const textOptionsFba = {
          x: 250,
          y: 450,
          size: 12,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          rotate: degrees(90)
        };
        copiedFbaPage.drawText(stampText, textOptionsFba);
      }

      finalPdf.addPage(copiedUpsPage);
      finalPdf.addPage(copiedFbaPage);
    }
  }

  // Clear cache
  shipmentPdfCache.clear();

  onProgress?.("Finalizing merged PDF...");
  // Save with optimization options
  return await finalPdf.save({
    useObjectStreams: false,
    addDefaultPage: false
  });
}

/**
 * Triggers a browser download for the provided PDF bytes.
 */
function downloadPdf(bytes, fileName = 'Merged UPS Labels.pdf') {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Main function that:
 * 1) Fetches the plan data internally.
 * 2) Logs the internal box contents.
 * 3) Processes all shipments using the plan mapping (computing the intersection).
 * 4) Merges and triggers download of the final merged PDF.
 */
export async function fetchUpsMergedLabels(placementOption, planId, onProgress, shipmentId = null) {
  try {
    onProgress?.("Fetching plan data...");
    const planData = await fetchPlanData(planId);
    console.log("Received plan data:", planData);
    logInternalBoxContents(planData);
    onProgress?.("Processing shipments using plan mapping...");
    const allBoxEntries = await processAllShipmentsUsingPlanMapping(placementOption, planData, onProgress);

    // Calculate total boxes from all shipments for logging
    const totalBoxes = placementOption?.shipments?.reduce((sum, shipment) => {
      return sum + (shipment.boxes?.length || 0);
    }, 0) || 0;

    console.log(`Processing ${totalBoxes} boxes across ${placementOption.shipments.length} shipments`);

    const mergedBytes = await mergeAllBoxes(allBoxEntries, onProgress);
    onProgress?.("Download ready...");
    const fileName = shipmentId ? `UPS_${shipmentId}.pdf` : 'UPS_Labels.pdf';
    downloadPdf(mergedBytes, fileName);
    onProgress?.("Download complete.");
  } catch (error) {
    console.error("Error while processing UPS labels:", error);
    onProgress?.(`Error: ${error.message}`);
    if (error.message.includes("No suitable label found for shipment")) {
      onProgress?.("Notification: UPS labels haven't been generated yet or shipments haven't been created yet.");
      alert("UPS labels haven't been generated yet or shipments haven't been created yet.");
    }
  }
}
